/**
 * Mixer - Console de mixage professionnelle
 *
 * Gère les canaux de mixage avec volume, mute, solo et sends
 * Architecture inspirée des consoles de mixage réelles (Reason, Ableton, etc.)
 */

import * as Tone from 'tone';

export class Mixer {
    constructor(outputNode, delayBus, reverbBus, chorusBus, phaserBus, flangerBus, crusherBus, tremoloBus, distortionBus, options = {}) {
        this.output = outputNode;
        this.delayBus = delayBus;
        this.reverbBus = reverbBus;
        this.chorusBus = chorusBus;
        this.phaserBus = phaserBus;
        this.flangerBus = flangerBus;
        this.crusherBus = crusherBus;
        this.tremoloBus = tremoloBus;
        this.distortionBus = distortionBus;
        this.channels = new Map();
        this.soloedChannels = new Set();
        this.metersEnabled = options.metersEnabled !== false;
        this.onSendChange = options.onSendChange || null;

        // Mapping des clés d'effet vers les propriétés de valeur de send
        this.sendValueKeys = {
            delay: 'delaySendValue',
            reverb: 'reverbSendValue',
            chorus: 'chorusSendValue',
            phaser: 'phaserSendValue',
            flanger: 'flangerSendValue',
            crusher: 'crusherSendValue',
            tremolo: 'tremoloSendValue',
            distortion: 'distortionSendValue'
        };
    }

    /**
     * Crée un nouveau canal de mixage
     * @param {string} id - Identifiant unique du canal (ex: 'tb303_1', 'tr808')
     * @returns {Tone.Gain} - Node d'entrée pour l'instrument
     */
    createChannel(id) {
        const channel = {
            id,
            input: new Tone.Gain(1),
            volumeNode: new Tone.Gain(1),
            // Meter for level monitoring (conditionally created)
            meter: this.metersEnabled ? new Tone.Meter({ smoothing: 0.8 }) : null,
            // Send gains (post-fader)
            delaySendGain: new Tone.Gain(0.2),
            reverbSendGain: new Tone.Gain(0.15),
            chorusSendGain: new Tone.Gain(0),
            phaserSendGain: new Tone.Gain(0),
            flangerSendGain: new Tone.Gain(0),
            crusherSendGain: new Tone.Gain(0),
            tremoloSendGain: new Tone.Gain(0),
            distortionSendGain: new Tone.Gain(0),
            // Valeurs stockées
            volumeValue: 1,
            delaySendValue: 0.2,
            reverbSendValue: 0.15,
            chorusSendValue: 0,
            phaserSendValue: 0,
            flangerSendValue: 0,
            crusherSendValue: 0,
            tremoloSendValue: 0,
            distortionSendValue: 0,
            muted: false,
            soloed: false
        };

        // Chaîne: input → volumeNode → output (master bus)
        channel.input.connect(channel.volumeNode);
        channel.volumeNode.connect(this.output);

        // Connect meter post-fader for level monitoring (if enabled)
        if (channel.meter) {
            channel.volumeNode.connect(channel.meter);
        }

        // Sends POST-FADER
        channel.volumeNode.connect(channel.delaySendGain);
        channel.delaySendGain.connect(this.delayBus);

        channel.volumeNode.connect(channel.reverbSendGain);
        channel.reverbSendGain.connect(this.reverbBus);

        channel.volumeNode.connect(channel.chorusSendGain);
        channel.chorusSendGain.connect(this.chorusBus);

        channel.volumeNode.connect(channel.phaserSendGain);
        channel.phaserSendGain.connect(this.phaserBus);

        channel.volumeNode.connect(channel.flangerSendGain);
        channel.flangerSendGain.connect(this.flangerBus);

        channel.volumeNode.connect(channel.crusherSendGain);
        channel.crusherSendGain.connect(this.crusherBus);

        channel.volumeNode.connect(channel.tremoloSendGain);
        channel.tremoloSendGain.connect(this.tremoloBus);

        channel.volumeNode.connect(channel.distortionSendGain);
        channel.distortionSendGain.connect(this.distortionBus);

        this.channels.set(id, channel);
        return channel.input;
    }

    /**
     * Retourne le node d'entrée d'un canal
     * @param {string} id - Identifiant du canal
     * @returns {Tone.Gain|null}
     */
    getChannelInput(id) {
        const channel = this.channels.get(id);
        return channel ? channel.input : null;
    }

    /**
     * Définit le volume d'un canal (0-1)
     */
    setVolume(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.volumeValue = Math.max(0, Math.min(1, value));
            this._updateChannelGain(channel);
            // Le volume affecte les niveaux effectifs des sends
            this._notifyAllSendChanges();
        }
    }

    /**
     * Définit le niveau de send delay d'un canal (0-1)
     */
    setDelaySend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.delaySendValue = Math.max(0, Math.min(1, value));
            channel.delaySendGain.gain.value = channel.delaySendValue;
            this._notifySendChange('delay');
        }
    }

    /**
     * Définit le niveau de send reverb d'un canal (0-1)
     */
    setReverbSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.reverbSendValue = Math.max(0, Math.min(1, value));
            channel.reverbSendGain.gain.value = channel.reverbSendValue;
            this._notifySendChange('reverb');
        }
    }

    /**
     * Définit le niveau de send chorus d'un canal (0-1)
     */
    setChorusSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.chorusSendValue = Math.max(0, Math.min(1, value));
            channel.chorusSendGain.gain.value = channel.chorusSendValue;
            this._notifySendChange('chorus');
        }
    }

    setPhaserSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.phaserSendValue = Math.max(0, Math.min(1, value));
            channel.phaserSendGain.gain.value = channel.phaserSendValue;
            this._notifySendChange('phaser');
        }
    }

    setFlangerSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.flangerSendValue = Math.max(0, Math.min(1, value));
            channel.flangerSendGain.gain.value = channel.flangerSendValue;
            this._notifySendChange('flanger');
        }
    }

    setCrusherSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.crusherSendValue = Math.max(0, Math.min(1, value));
            channel.crusherSendGain.gain.value = channel.crusherSendValue;
            this._notifySendChange('crusher');
        }
    }

    setTremoloSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.tremoloSendValue = Math.max(0, Math.min(1, value));
            channel.tremoloSendGain.gain.value = channel.tremoloSendValue;
            this._notifySendChange('tremolo');
        }
    }

    setDistortionSend(id, value) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.distortionSendValue = Math.max(0, Math.min(1, value));
            channel.distortionSendGain.gain.value = channel.distortionSendValue;
            this._notifySendChange('distortion');
        }
    }

    /**
     * Active/désactive le mute d'un canal
     */
    setMute(id, muted) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.muted = muted;
            this._updateChannelGain(channel);
        }
    }

    /**
     * Active/désactive le solo d'un canal
     * Quand un canal est en solo, tous les autres sont mutés (sauf ceux aussi en solo)
     */
    setSolo(id, soloed) {
        const channel = this.channels.get(id);
        if (channel) {
            channel.soloed = soloed;

            if (soloed) {
                this.soloedChannels.add(id);
            } else {
                this.soloedChannels.delete(id);
            }

            // Mettre à jour tous les canaux car le solo affecte tout le monde
            this._updateAllChannelGains();
        }
    }

    /**
     * Met à jour le gain effectif d'un canal en fonction de mute/solo
     */
    _updateChannelGain(channel) {
        const hasSoloActive = this.soloedChannels.size > 0;

        if (channel.muted) {
            // Mute a priorité
            channel.volumeNode.gain.value = 0;
        } else if (hasSoloActive && !channel.soloed) {
            // Si un autre canal est en solo et celui-ci ne l'est pas → muté
            channel.volumeNode.gain.value = 0;
        } else {
            // Volume normal
            channel.volumeNode.gain.value = channel.volumeValue;
        }
    }

    /**
     * Met à jour tous les canaux (appelé quand le solo change)
     */
    _updateAllChannelGains() {
        for (const channel of this.channels.values()) {
            this._updateChannelGain(channel);
        }
    }

    // --- Getters pour l'UI ---

    getVolume(id) {
        const channel = this.channels.get(id);
        return channel ? channel.volumeValue : 0;
    }

    getDelaySend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.delaySendValue : 0;
    }

    getReverbSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.reverbSendValue : 0;
    }

    getChorusSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.chorusSendValue : 0;
    }

    getPhaserSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.phaserSendValue : 0;
    }

    getFlangerSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.flangerSendValue : 0;
    }

    getCrusherSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.crusherSendValue : 0;
    }

    getTremoloSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.tremoloSendValue : 0;
    }

    getDistortionSend(id) {
        const channel = this.channels.get(id);
        return channel ? channel.distortionSendValue : 0;
    }

    isMuted(id) {
        const channel = this.channels.get(id);
        return channel ? channel.muted : false;
    }

    isSoloed(id) {
        const channel = this.channels.get(id);
        return channel ? channel.soloed : false;
    }

    /**
     * Retourne le niveau actuel du mètre (en dB)
     * @param {string} id - Identifiant du canal
     * @returns {number} - Niveau en dB (-Infinity à ~0)
     */
    getMeterLevel(id) {
        const channel = this.channels.get(id);
        return (channel && channel.meter) ? channel.meter.getValue() : -Infinity;
    }

    /**
     * Calcule le total effectif des sends pour un effet donné
     * (prend en compte le volume de chaque canal)
     * @param {string} effectKey - Clé de l'effet (delay, reverb, chorus, etc.)
     * @returns {number} - Somme pondérée des sends
     */
    getEffectiveSendTotal(effectKey) {
        const key = this.sendValueKeys[effectKey];
        if (!key) return 0;

        let total = 0;
        for (const channel of this.channels.values()) {
            const sendValue = channel[key] || 0;
            const volume = channel.volumeNode.gain.value || 0;
            total += volume * sendValue;
        }
        return total;
    }

    /**
     * Notifie le changement d'un send spécifique
     * @param {string} effectKey - Clé de l'effet
     */
    _notifySendChange(effectKey) {
        if (!this.onSendChange) return;
        this.onSendChange(effectKey, this.getEffectiveSendTotal(effectKey));
    }

    /**
     * Notifie le changement de tous les sends
     */
    _notifyAllSendChanges() {
        if (!this.onSendChange) return;
        Object.keys(this.sendValueKeys).forEach((key) => {
            this.onSendChange(key, this.getEffectiveSendTotal(key));
        });
    }

    /**
     * Retourne l'état complet d'un canal (pour sauvegarde)
     */
    getChannelState(id) {
        const channel = this.channels.get(id);
        if (!channel) return null;

        return {
            volume: channel.volumeValue,
            delaySend: channel.delaySendValue,
            reverbSend: channel.reverbSendValue,
            chorusSend: channel.chorusSendValue,
            phaserSend: channel.phaserSendValue,
            flangerSend: channel.flangerSendValue,
            crusherSend: channel.crusherSendValue,
            tremoloSend: channel.tremoloSendValue,
            distortionSend: channel.distortionSendValue,
            muted: channel.muted,
            soloed: channel.soloed
        };
    }

    /**
     * Charge l'état d'un canal (depuis sauvegarde)
     */
    setChannelState(id, state) {
        if (state.volume !== undefined) this.setVolume(id, state.volume);
        if (state.delaySend !== undefined) this.setDelaySend(id, state.delaySend);
        if (state.reverbSend !== undefined) this.setReverbSend(id, state.reverbSend);
        if (state.chorusSend !== undefined) this.setChorusSend(id, state.chorusSend);
        if (state.phaserSend !== undefined) this.setPhaserSend(id, state.phaserSend);
        if (state.flangerSend !== undefined) this.setFlangerSend(id, state.flangerSend);
        if (state.crusherSend !== undefined) this.setCrusherSend(id, state.crusherSend);
        if (state.tremoloSend !== undefined) this.setTremoloSend(id, state.tremoloSend);
        if (state.distortionSend !== undefined) this.setDistortionSend(id, state.distortionSend);
        if (state.muted !== undefined) this.setMute(id, state.muted);
    }

    /**
     * Retourne tous les IDs de canaux
     */
    getChannelIds() {
        return Array.from(this.channels.keys());
    }
}
